import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { useMe } from "@/features/auth";
import { ProposalsRow } from "@/features/challenges";
import { CrewFeed, useJournal, useToday } from "@/features/checkins";
import { errorMessage } from "@/i18n/errors";
import { DAY_MS } from "@/shared/lib/dates";
import { Banner, Button, Icon, Screen, Skeleton } from "@/shared/ui";

import { useCrew } from "../api";
import { CrewStories } from "../components/CrewStories";
import styles from "../crew.module.css";
import { freshCounts, markSeen, useSeenVersion } from "../seen";

/**
 * Echipa, the crew's journal: everyone's day as a row of rings (each opens their page), the
 * proposals when there are some, then day by day what the crew did. Members and invites are one
 * tap away, behind the button next to the title.
 */
export function CrewRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const me = useMe();
  const crew = useCrew();
  const today = useToday();
  const journal = useJournal();
  useSeenVersion();
  // Never looked at someone's proofs on this device: the last day's count as new.
  const [since] = useState(() => new Date(Date.now() - DAY_MS));
  const isAdmin = me.data?.member?.role === "admin";
  const meId = me.data?.member?.id;
  const items = journal.data?.pages.flatMap((page) => page.results) ?? [];
  const fresh = crew.data ? freshCounts(items, crew.data.id, meId, since) : {};

  return (
    <Screen
      title={crew.data?.name ?? t("crew.title")}
      action={
        <div className={styles.headerActions}>
          <Button
            variant="ghost"
            aria-label={t("news.open")}
            icon={<Icon name="bell" size={22} />}
            onClick={() => navigate("/crew/news")}
          />
          <Button
            variant="ghost"
            aria-label={t("crew.members.open")}
            icon={<Icon name="users" size={22} />}
            onClick={() => navigate("/crew/members")}
          />
        </div>
      }
    >
      {crew.isPending && <Skeleton lines={4} />}
      {crew.isError && (
        <Banner
          tone="danger"
          title={errorMessage(t, crew.error)}
          actions={
            <Button variant="ghost" onClick={() => void crew.refetch()}>
              {t("common.retry")}
            </Button>
          }
        />
      )}
      {crew.data && (
        <>
          <CrewStories
            members={crew.data.members}
            meId={meId}
            today={today.data?.crew}
            fresh={fresh}
          />
          <ProposalsRow isAdmin={isAdmin} timeZone={crew.data.timezone} />
          <CrewFeed
            timeZone={crew.data.timezone}
            members={crew.data.members}
            onOpenProofOf={(memberId) => crew.data && markSeen(crew.data.id, memberId)}
          />
        </>
      )}
    </Screen>
  );
}
